import React, { useEffect, useState } from 'react';
import {
  Typography,
  Card,
  Row,
  Col,
  Segmented,
  Image,
  Tag,
  Spin,
  Alert,
  Steps,
  Divider,
  Space,
  message,
  Button,
  Input,
} from 'antd';
import {
  AlipayCircleOutlined,
  WechatOutlined,
  PayCircleOutlined,
  SafetyCertificateOutlined,
  CustomerServiceOutlined,
  CheckCircleOutlined,
  CameraOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import { useLanguage } from '../store/LanguageContext';
import { supportEmail } from '../utils/site';
import { useSearchParams } from 'react-router-dom';
import { orderAPI, paymentAPI } from '../services/api';
import type { Order, PaymentMethodItem, PaymentPriceInfo } from '../types';

const { Title, Paragraph, Text } = Typography;

/* ==================== 图标映射 ==================== */
const iconMap: Record<string, React.ReactNode> = {
  AlipayCircleOutlined: <AlipayCircleOutlined style={{ fontSize: 28 }} />,
  WechatOutlined: <WechatOutlined style={{ fontSize: 28 }} />,
  PayCircleOutlined: <PayCircleOutlined style={{ fontSize: 28 }} />,
};

/* ==================== 套餐配置 ==================== */
const getPlanLabel = (plan: string, t: (k: string) => string) => {
  const map: Record<string, string> = {
    free: t('payment.planFree'),
    standard: t('payment.planStandard'),
    premium: t('payment.planPremium'),
  };
  return map[plan] || plan;
};

/* ==================== PaymentPage ==================== */
const PaymentPage: React.FC = () => {
  const { language, t } = useLanguage();
  const [searchParams] = useSearchParams();

  const isZh = language === 'zh-CN';
  const planFromUrl = searchParams.get('plan') || 'standard';

  const [currentPlan, setCurrentPlan] = useState<string>(planFromUrl);
  const [methods, setMethods] = useState<PaymentMethodItem[]>([]);
  const [price, setPrice] = useState<PaymentPriceInfo | null>(null);
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedMethod, setSelectedMethod] = useState<string>('');
  const [currentOrder, setCurrentOrder] = useState<Order | null>(null);
  const [contactInfo, setContactInfo] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [submittingPaid, setSubmittingPaid] = useState(false);

  // ---- 根据语言 + 套餐获取支付方式 ----
  useEffect(() => {
    const fetchMethods = async () => {
      setLoading(true);
      try {
        const [res, orderRes] = await Promise.all([
          paymentAPI.getPaymentMethods({ lang: language, plan: currentPlan }),
          orderAPI.createPublic({
            product_id: currentPlan,
            plan_type: currentPlan,
            tool_name: 'AI Tools Subscription',
          }),
        ]);
        const data = res.data;
        setMethods(data.methods || []);
        setPrice(data.price || null);
        setNotice(data.notice || '');
        setCurrentOrder(orderRes.data.order || null);
        // 默认选中第一个
        if (data.methods?.length > 0) {
          setSelectedMethod(data.methods[0].key);
        }
      } catch {
        message.error(t('common.operationFailed'));
      } finally {
        setLoading(false);
      }
    };
    fetchMethods();
  }, [language, currentPlan, t]);

  const selectedInfo = methods.find((m) => m.key === selectedMethod);

  const handleSubmitPaid = async () => {
    if (!currentOrder) {
      message.error(isZh ? '订单尚未创建，请稍后重试' : 'Order not ready yet');
      return;
    }

    setSubmittingPaid(true);
    try {
      const res = await orderAPI.submitPublicPayment(currentOrder.id, {
        user_contact: contactInfo,
        contact_info: contactInfo,
        payment_method: selectedMethod,
        payment_reference: paymentReference,
      });
      setCurrentOrder(res.data.order || currentOrder);
      message.success(isZh ? '已提交支付信息，请等待人工审核' : 'Payment info submitted, pending manual review');
    } catch {
      message.error(t('common.operationFailed'));
    } finally {
      setSubmittingPaid(false);
    }
  };

  /* ==================== 套餐切换选项 ==================== */
  const planOptions = [
    { label: getPlanLabel('standard', t), value: 'standard' },
    { label: getPlanLabel('premium', t), value: 'premium' },
  ];

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', padding: '48px 20px 64px' }}>
      {/* ===== 页面标题 ===== */}
      <div style={{ textAlign: 'center', marginBottom: 40 }}>
        <Title level={2} style={{ marginBottom: 8 }}>
          {t('payment.title')}
        </Title>
        <Paragraph style={{ color: '#666', fontSize: 16 }}>
          {t('payment.subtitle')}
        </Paragraph>
      </div>

      {/* ===== 套餐切换 ===== */}
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <Segmented
          options={planOptions}
          value={currentPlan}
          onChange={(val) => setCurrentPlan(val as string)}
          size="large"
          style={{ padding: 4 }}
        />
      </div>

      {/* ===== 价格展示 ===== */}
      {price && (
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <Text style={{ fontSize: 48, fontWeight: 700, color: '#1677ff' }}>
            {price.display}
          </Text>
          <br />
          <Tag color="blue" style={{ fontSize: 14, marginTop: 8 }}>
            {getPlanLabel(currentPlan, t)}
          </Tag>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 80 }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          {/* ===== 支付方式选择 ===== */}
          <Title level={4} style={{ marginBottom: 16 }}>
            {t('payment.selectMethod')}
          </Title>

          <Row gutter={[16, 16]} style={{ marginBottom: 32 }}>
            {methods.map((m) => (
              <Col key={m.key} xs={24} sm={methods.length === 1 ? 24 : 12}>
                <Card
                  hoverable
                  onClick={() => setSelectedMethod(m.key)}
                  style={{
                    borderRadius: 12,
                    border: selectedMethod === m.key
                      ? `2px solid ${m.color}`
                      : '2px solid #f0f0f0',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    boxShadow: selectedMethod === m.key
                      ? `0 4px 20px ${m.color}22`
                      : '0 2px 8px rgba(0,0,0,0.04)',
                  }}
                  bodyStyle={{ padding: '20px 24px' }}
                >
                  <Space size={12} align="center">
                    <span style={{ color: m.color }}>
                      {iconMap[m.icon] || <PayCircleOutlined style={{ fontSize: 28 }} />}
                    </span>
                    <div>
                      <Text strong style={{ fontSize: 16 }}>{m.name}</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 13 }}>{m.description}</Text>
                    </div>
                  </Space>
                  {selectedMethod === m.key && (
                    <CheckCircleOutlined
                      style={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        fontSize: 20,
                        color: m.color,
                      }}
                    />
                  )}
                </Card>
              </Col>
            ))}
          </Row>

          {/* ===== 二维码展示 ===== */}
          {selectedInfo && (
            <Card
              style={{
                borderRadius: 16,
                textAlign: 'center',
                marginBottom: 32,
                background: 'linear-gradient(135deg, #fafbff 0%, #f5f0ff 100%)',
                border: `1px solid ${selectedInfo.color}33`,
              }}
              bodyStyle={{ padding: '40px 24px' }}
            >
              <div style={{ marginBottom: 16 }}>
                <Tag
                  color={selectedInfo.color}
                  style={{ fontSize: 15, padding: '4px 16px', borderRadius: 20 }}
                >
                  {selectedInfo.name}
                </Tag>
              </div>

              <div
                style={{
                  display: 'inline-block',
                  padding: 16,
                  background: '#fff',
                  borderRadius: 12,
                  boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
                  marginBottom: 20,
                }}
              >
                <Image
                  src={selectedInfo.qrCode}
                  alt={`${selectedInfo.name} QR Code`}
                  width={220}
                  height={220}
                  style={{ borderRadius: 8 }}
                  fallback="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMjAiIGhlaWdodD0iMjIwIiB2aWV3Qm94PSIwIDAgMjIwIDIyMCI+PHJlY3Qgd2lkdGg9IjIyMCIgaGVpZ2h0PSIyMjAiIGZpbGw9IiNmNWY1ZjUiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZm9udC1zaXplPSIxNiIgZmlsbD0iIzk5OSI+UVIgQ29kZTwvdGV4dD48L3N2Zz4="
                  preview={false}
                />
              </div>

              <Paragraph style={{ color: '#666', fontSize: 14, marginBottom: 0 }}>
                {t('payment.scanTip')}
              </Paragraph>

              {selectedInfo?.pay_link && (
                <Button type="link" href={selectedInfo.pay_link} target="_blank" style={{ marginTop: 8 }}>
                  {isZh ? '打开支付链接' : 'Open payment link'}
                </Button>
              )}

              {price && (
                <Title level={3} style={{ color: selectedInfo.color, marginTop: 12, marginBottom: 0 }}>
                  {t('payment.amountLabel')}: {price.display}
                </Title>
              )}
            </Card>
          )}

          <Card style={{ borderRadius: 12, marginBottom: 24 }} bodyStyle={{ padding: 20 }}>
            <Title level={5} style={{ marginBottom: 12 }}>
              {isZh ? '提交支付信息（人工审核）' : 'Submit payment details (manual review)'}
            </Title>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Input
                placeholder={isZh ? '联系方式（邮箱/手机）' : 'Contact (email/phone)'}
                value={contactInfo}
                onChange={(e) => setContactInfo(e.target.value)}
              />
              <Input
                placeholder={isZh ? '支付订单号（可选）' : 'Payment reference (optional)'}
                value={paymentReference}
                onChange={(e) => setPaymentReference(e.target.value)}
              />
              <Button type="primary" loading={submittingPaid} onClick={handleSubmitPaid}>
                {isZh ? '我已支付，提交审核' : 'I have paid, submit for review'}
              </Button>
              {currentOrder && (
                <Text type="secondary">
                  {isZh ? `订单号：${currentOrder.order_no}（状态：${currentOrder.status || currentOrder.payment_status}）` : `Order No: ${currentOrder.order_no} (Status: ${currentOrder.status || currentOrder.payment_status})`}
                </Text>
              )}
            </Space>
          </Card>

          {/* ===== 支付流程提示 ===== */}
          <Card
            style={{ borderRadius: 12, marginBottom: 32 }}
            bodyStyle={{ padding: '24px 32px' }}
          >
            <Title level={5} style={{ marginBottom: 16 }}>
              {t('payment.stepsTitle')}
            </Title>
            <Steps
              direction="vertical"
              size="small"
              current={-1}
              items={[
                {
                  title: t('payment.step1Title'),
                  description: t('payment.step1Desc'),
                  icon: <PayCircleOutlined />,
                },
                {
                  title: t('payment.step2Title'),
                  description: t('payment.step2Desc'),
                  icon: <CameraOutlined />,
                },
                {
                  title: t('payment.step3Title'),
                  description: t('payment.step3Desc'),
                  icon: <CustomerServiceOutlined />,
                },
                {
                  title: t('payment.step4Title'),
                  description: t('payment.step4Desc'),
                  icon: <CheckCircleOutlined />,
                },
              ]}
            />
          </Card>

          {/* ===== 温馨提示 ===== */}
          {notice && (
            <Alert
              message={t('payment.noticeTitle')}
              description={notice}
              type="info"
              showIcon
              style={{ borderRadius: 12, marginBottom: 32 }}
            />
          )}

          {/* ===== 联系客服 ===== */}
          <Card
            style={{
              borderRadius: 12,
              background: 'linear-gradient(135deg, #f0f5ff 0%, #e6fffb 100%)',
              border: 'none',
            }}
            bodyStyle={{ padding: '24px 32px' }}
          >
            <Row gutter={24} align="middle">
              <Col flex="auto">
                <Space size={12} align="start">
                  <CustomerServiceOutlined style={{ fontSize: 32, color: '#1677ff' }} />
                  <div>
                    <Text strong style={{ fontSize: 16 }}>{t('payment.supportTitle')}</Text>
                    <br />
                    <Text type="secondary">{t('payment.supportDesc')}</Text>
                  </div>
                </Space>
              </Col>
              <Col>
                <Button
                  type="default"
                  icon={<CopyOutlined />}
                  onClick={() => {
                    navigator.clipboard.writeText(supportEmail());
                    message.success(t('payment.emailCopied'));
                  }}
                >
                  {t('payment.copyEmail')}
                </Button>
              </Col>
            </Row>
          </Card>

          <Divider />

          {/* ===== 安全保障 ===== */}
          <div style={{ textAlign: 'center', padding: '0 0 32px' }}>
            <Space size={24}>
              <Space size={6}>
                <SafetyCertificateOutlined style={{ color: '#52c41a' }} />
                <Text type="secondary">{t('payment.guarantee1')}</Text>
              </Space>
              <Space size={6}>
                <CustomerServiceOutlined style={{ color: '#1677ff' }} />
                <Text type="secondary">{t('payment.guarantee2')}</Text>
              </Space>
              <Space size={6}>
                <CheckCircleOutlined style={{ color: '#faad14' }} />
                <Text type="secondary">{t('payment.guarantee3')}</Text>
              </Space>
            </Space>
          </div>
        </>
      )}
    </div>
  );
};

export default PaymentPage;
